import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faMousePointer, faUser } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { OptionProps, SingleValueProps } from 'react-select-modern';
import { components } from 'react-select-modern';

import { FieldCollection } from '@feathr/blackbox';
import { UniversalSelect } from '@feathr/components';

import * as styles from './CustomFieldsCollectionSelect.css';

interface IProps {
  className?: string;
  onChange: (newValue: FieldCollection) => void;
  value?: FieldCollection;
}

interface IFieldCollectionOption {
  id: FieldCollection;
  name: string;
}

const collectionToIconMap = new Map<FieldCollection, IconProp>([
  [FieldCollection.Person, faUser],
  [FieldCollection.Breadcrumb, faMousePointer],
]);

function Option(props: OptionProps<IFieldCollectionOption>) {
  const { data } = props;
  const icon = collectionToIconMap.get(data.id);
  return (
    <components.Option {...props} className={styles.option}>
      {icon && <FontAwesomeIcon icon={icon} />}
      <span>{data.name}</span>
    </components.Option>
  );
}

function SingleValue(props: SingleValueProps<IFieldCollectionOption>) {
  const { data } = props;
  const icon = collectionToIconMap.get(data.id);
  return (
    <components.SingleValue {...props} className={styles.option}>
      {icon && <FontAwesomeIcon icon={icon} />}
      <span>{data.name}</span>
    </components.SingleValue>
  );
}

function CustomFieldsCollectionSelect({ className, onChange, value }: IProps) {
  const { t } = useTranslation();

  const fieldCollectionOptions: IFieldCollectionOption[] = [
    { id: FieldCollection.Person, name: t('Attribute Fields') },
    { id: FieldCollection.Breadcrumb, name: t('Activity Fields') },
  ];

  const currentFieldCollection =
    fieldCollectionOptions.find((option) => option.id === value) || fieldCollectionOptions[0];

  function handleChange(option: IFieldCollectionOption): void {
    onChange(option.id);
  }

  return (
    <UniversalSelect<IFieldCollectionOption>
      className={className}
      components={{ Option, SingleValue }}
      name={'custom-fields-collection-select'}
      onSelectSingle={handleChange}
      options={fieldCollectionOptions}
      value={currentFieldCollection}
      wrapperClassName={styles.root}
    />
  );
}

export default CustomFieldsCollectionSelect;
